/**
 * 会话行视图模型（纯函数，任务 8d-2）：ConversationRecord → 行渲染所需字段。
 * 组件（ConversationList.tsx）保持纯展示，口径都锁在这里：
 * - 名称：friendNote（好友备注，仅私聊有值）优先，其次 nickname；
 *   都空 = 占位行等补拉/对账（8c-2），渲染空名 + '?' 头像字符，不造假名。
 * - 预览：lastMsg 容忍空串（44 贴纸本地落库为空，对账后覆盖——8b-1 刻意偏差）。
 * - 角标：unread>0 才有，>99 显示 99+；noDisturbing 弱化样式（muted）不隐藏。
 * - @ 标记：isAt 时预览前加红字 [有人@我]（服务端 IsAt 口径，群文本 @我/@全员）。
 * - 草稿（14.12）：有草稿时预览换成草稿文本 + [草稿] 红字装饰（@标记保留——
 *   isAt 是未读@的服务端信号，不被草稿盖掉）；时间与排序按 max(lastMsgTimestamp,
 *   draft.updatedAt) bump——**不改 store 数据**，只在渲染层生效（会话数据不被
 *   草稿污染，清草稿即还原）。isTop 分组不受草稿影响（置顶仍压非置顶）。
 * - VIP 徽标（21g）：私聊看 remote.vipLevel、群聊看 remote.groupLevel（互斥），
 *   档位与文案单点在 `~/chat/vipBadge`（后端 UserVipLevels / VipLevels 为准）。
 *   null = 非 VIP 不渲染。数据早就在库里（api.ts 一直在解析），只是此前没人读。
 * - 加密（14.14b）：remote.isEncrypted≠0（最后一条消息是端到端加密）时预览
 *   恒显「[加密消息]」——服务端 last_msg 是密文截断、本地 bump 的预览可能是
 *   明文，两路都不外显（旧版 _buildLastMsgText 同口径）。草稿优先级更高
 *   （草稿是本地明文输入，不受加密位影响）。
 */
import { resolveMediaUrl } from '~/chat/mediaUrl';
import { conversationVipBadge, type VipBadgeView } from '~/chat/vipBadge';
import { sendStatusOf, type SendStatus } from '~/chat/readState';
import { formatConversationTime } from '~/chat/formatTime';
import { compareConversations } from '~/chat/ordering';
import type { DraftEntry, DraftsSlice } from '~/store/draftsState';
import type { ConversationRecord } from '~/worker/contract';

export interface ConversationRowView {
  readonly key: string;
  readonly name: string;
  /** 空串 = 无头像/cdn 未就绪，渲染 avatarChar 占位 */
  readonly avatarUrl: string;
  /** 名称首字符（码点级，不劈 emoji/生僻字）；无名时 '?' */
  readonly avatarChar: string;
  readonly preview: string;
  readonly mentioned: boolean;
  /** 有草稿：preview 已是草稿文本，渲染层加 [草稿] 标记 */
  readonly draft: boolean;
  readonly time: string;
  readonly pinned: boolean;
  readonly badge: { readonly text: string; readonly muted: boolean } | null;
  /** VIP / 群 VIP / 官方客服徽标（21g）；null = 非 VIP */
  readonly vip: VipBadgeView | null;
  /**
   * 末条消息的发送态标记（21v，旧版 chat_list_item `_statusIcon` 同位）：
   * `null` = 不显示（末条不是我发的 / 群聊 / 有草稿）。
   * 群聊不显示的理由见 `chat/readState` 文件头。
   */
  readonly sendStatus: SendStatus | null;
}

export function conversationRowView(
  record: ConversationRecord,
  cdnBase: string,
  nowMs: number,
  draft?: DraftEntry | null,
  /** 自己的 uid（21v）：判断末条是不是我发的；0=未知则不显示状态标记 */
  myUid = 0,
): ConversationRowView {
  const { remote } = record;
  const name = remote.friendNote || remote.nickname;
  const hasDraft = draft != null;
  return {
    key: record.key,
    name,
    avatarUrl: resolveMediaUrl(cdnBase, remote.avatar),
    avatarChar: [...name][0] ?? '?',
    preview: hasDraft ? draft.text : remote.isEncrypted !== 0 ? '[加密消息]' : remote.lastMsg,
    mentioned: remote.isAt !== 0,
    draft: hasDraft,
    time: formatConversationTime(
      hasDraft && draft.updatedAt > remote.lastMsgTimestamp
        ? draft.updatedAt
        : remote.lastMsgTimestamp,
      nowMs,
    ),
    pinned: remote.isTop !== 0,
    // 草稿态下 preview 显示的是草稿、时间也是草稿时间，再挂末条消息的发送态会错位
    sendStatus:
      hasDraft || myUid === 0 || remote.lastSendUid !== myUid
        ? null
        : sendStatusOf(
            { id: remote.lastMsgId, outgoing: true },
            remote.peerLastReadId,
            record.key.startsWith('group:'),
          ),
    vip: conversationVipBadge(record.key.startsWith('group:'), remote.vipLevel, remote.groupLevel),
    badge:
      remote.unread > 0
        ? {
            text: remote.unread > 99 ? '99+' : String(remote.unread),
            muted: remote.noDisturbing !== 0,
          }
        : null,
  };
}

/**
 * 草稿 bump 排序（14.12）：有草稿的行按 max(lastMsgTimestamp, draft.updatedAt)
 * 参与 compareConversations（isTop 分组保持）。无命中草稿时原引用直返——
 * rows 本就按 compareConversations 排好（conversationsState），不白排。
 */
export function orderRowsWithDrafts(
  rows: readonly ConversationRecord[],
  drafts: DraftsSlice,
): readonly ConversationRecord[] {
  if (drafts === null) return rows;
  const keyOf = (r: ConversationRecord) => {
    const d = drafts[r.key];
    return d !== undefined && d.updatedAt > r.remote.lastMsgTimestamp
      ? { ...r.remote, lastMsgTimestamp: d.updatedAt }
      : r.remote;
  };
  if (!rows.some((r) => drafts[r.key] !== undefined)) return rows;
  return [...rows].sort((a, b) => compareConversations(keyOf(a), keyOf(b)));
}
